import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DatePicker, Form, Input, Modal, notification, Radio, Select, Switch } from "antd";
import { coreApi } from "../../api/calls";
import CitiesSelector from "../Cities/CitiesSelector";
import moment from "moment";

const { TextArea } = Input;

const CampaignEditForm = ({ campaign, onSuccess, onCancel }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [periodType, setPeriodType] = useState("custom");

  useEffect(() => {
    if (campaign) {
      let detectedPeriodType = "quarter";
      let quarterValue = undefined;
      let monthValue = undefined;
      
      if (campaign.start_date && campaign.end_date) {
        const startMoment = moment(campaign.start_date);
        const endMoment = moment(campaign.end_date);
        
        const quarters = {
          Q1: { start: "01-01", end: "03-31" },
          Q2: { start: "04-01", end: "06-30" },
          Q3: { start: "07-01", end: "09-30" },
          Q4: { start: "10-01", end: "12-31" },
        };
        
        // Try to detect which quarter based on dates
        for (const [quarter, dates] of Object.entries(quarters)) {
          if (startMoment.format("MM-DD") === dates.start && endMoment.format("MM-DD") === dates.end) {
            detectedPeriodType = "quarter";
            quarterValue = quarter;
            break;
          }
        }
        
        // If no exact quarter match, check if it's a month
        if (!quarterValue) {
          if (startMoment.date() === 1 && endMoment.isSame(startMoment.clone().endOf("month"), "day")) {
            detectedPeriodType = "month";
            monthValue = startMoment.format("YYYY-MM");
          } else {
            detectedPeriodType = "custom";
          }
        }
      }
      
      setPeriodType(detectedPeriodType);
      
      form.setFieldsValue({
        name: campaign.name,
        description: campaign.description,
        is_active: campaign.is_active,
        is_recurring: campaign.is_recurring !== undefined ? campaign.is_recurring : true,
        city_branches: campaign.city_branches?.map((city) => city.id) || [],
        quarter: quarterValue,
        month: monthValue ? moment(monthValue, "YYYY-MM") : undefined,
        start_date: campaign.start_date ? moment(campaign.start_date) : undefined,
        end_date: campaign.end_date ? moment(campaign.end_date) : undefined,
      });
    }
  }, [campaign, form]);

  const handleDateChange = (dates, dateStrings, field) => {
    // Auto-detect quarter when both dates are selected in custom mode
    if (periodType === "custom" && field === "end_date") {
      const startDate = form.getFieldValue("start_date");
      const endDate = dates;
      
      if (startDate && endDate) {
        const startMoment = moment(startDate);
        const endMoment = moment(endDate);
        
        const quarters = {
          Q1: { start: "01-01", end: "03-31" },
          Q2: { start: "04-01", end: "06-30" },
          Q3: { start: "07-01", end: "09-30" },
          Q4: { start: "10-01", end: "12-31" },
        };
        
        // Check if dates match any quarter
        for (const [quarter, quarterDates] of Object.entries(quarters)) {
          if (
            startMoment.format("MM-DD") === quarterDates.start && 
            endMoment.format("MM-DD") === quarterDates.end &&
            startMoment.year() === endMoment.year()
          ) {
            // Auto-switch to quarter mode and select the quarter
            setPeriodType("quarter");
            form.setFieldsValue({
              quarter: quarter,
              start_date: undefined,
              end_date: undefined,
            });
            break;
          }
        }
      }
    }
  };

  const handleSubmit = async (values) => {
    setIsSubmitting(true);
    try {
      // Calculate start_date and end_date based on period type
      let startDate, endDate;
      
      if (periodType === "quarter") {
        const year = moment().year();
        const quarters = {
          Q1: { from: `${year}-01-01`, to: `${year}-03-31` },
          Q2: { from: `${year}-04-01`, to: `${year}-06-30` },
          Q3: { from: `${year}-07-01`, to: `${year}-09-30` },
          Q4: { from: `${year}-10-01`, to: `${year}-12-31` },
        };
        startDate = quarters[values.quarter].from;
        endDate = quarters[values.quarter].to;
      } else if (periodType === "month") {
        const monthMoment = moment(values.month);
        startDate = monthMoment.format("YYYY-MM-DD");
        endDate = monthMoment.endOf("month").format("YYYY-MM-DD");
      } else {
        startDate = values.start_date.format("YYYY-MM-DD");
        endDate = values.end_date.format("YYYY-MM-DD");
      }

      await coreApi.put(`/campaigns/${campaign.id}`, {
        name: values.name,
        description: values.description,
        is_active: values.is_active,
        is_recurring: values.is_recurring,
        start_date: startDate,
        end_date: endDate,
        period_type: periodType, // quarter, month, или custom
        city_branch_ids: values.city_branches,
      });
      
      notification.success({
        message: t("campaigns.updateSuccess"),
      });
      onSuccess();
    } catch (error) {
      console.error(error);
      notification.error({
        message: error?.response?.data?.message || t("campaigns.updateError"),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      width={700}
      title={t("campaigns.edit")}
      open={true}
      onCancel={onCancel}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      okButtonProps={{ loading: isSubmitting }}
      onOk={() => form.submit()}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
      >
        <Form.Item
          name="name"
          label={t("campaigns.name")}
          rules={[
            { required: true, message: t("campaigns.nameRequired") },
            { min: 3, message: t("campaigns.nameMinLength") },
            { max: 255, message: t("campaigns.nameMaxLength") },
          ]}
        >
          <Input placeholder={t("campaigns.namePlaceholder")} />
        </Form.Item>

        <Form.Item
          name="description"
          label={t("campaigns.description")}
          rules={[{ max: 500, message: t("campaigns.descriptionMaxLength") }]}
        >
          <TextArea
            rows={4}
            placeholder={t("campaigns.descriptionPlaceholder")}
            showCount
            maxLength={500}
          />
        </Form.Item>

        {/* Period Type Selection */}
        <Form.Item label={t("campaigns.periodType")}>
          <Radio.Group 
            value={periodType} 
            onChange={(e) => {
              setPeriodType(e.target.value);
              // Reset period-specific fields
              form.setFieldsValue({
                quarter: undefined,
                month: undefined,
                start_date: undefined,
                end_date: undefined,
              });
            }}
          >
            <Radio value="quarter">{t("campaigns.periodQuarter")}</Radio>
            <Radio value="month">{t("campaigns.periodMonth")}</Radio>
            <Radio value="custom">{t("campaigns.periodCustom")}</Radio>
          </Radio.Group>
        </Form.Item>

        {/* Quarter Selection */}
        {periodType === "quarter" && (
          <Form.Item
            name="quarter"
            label={t("campaigns.selectQuarter")}
            rules={[{ required: true, message: t("campaigns.selectPeriodRequired") }]}
          >
            <Select placeholder={t("campaigns.selectQuarterPlaceholder")}>
              <Select.Option value="Q1">Q1 (01.01 - 31.03)</Select.Option>
              <Select.Option value="Q2">Q2 (01.04 - 30.06)</Select.Option>
              <Select.Option value="Q3">Q3 (01.07 - 30.09)</Select.Option>
              <Select.Option value="Q4">Q4 (01.10 - 31.12)</Select.Option>
            </Select>
          </Form.Item>
        )}

        {/* Month Selection */}
        {periodType === "month" && (
          <Form.Item
            name="month"
            label={t("campaigns.selectMonth")}
            rules={[{ required: true, message: t("campaigns.selectPeriodRequired") }]}
          >
            <DatePicker
              picker="month"
              style={{ width: "100%" }}
              placeholder={t("campaigns.selectMonthPlaceholder")}
              format="YYYY-MM"
            />
          </Form.Item>
        )}

        {/* Custom Date Range */}
        {periodType === "custom" && (
          <>
            <Form.Item
              name="start_date"
              label={t("campaigns.dateFrom")}
              rules={[{ required: true, message: t("campaigns.selectDatesRequired") }]}
            >
              <DatePicker
                style={{ width: "100%" }}
                placeholder={t("campaigns.selectDateFrom")}
                onChange={(date) => handleDateChange(date, null, "start_date")}
              />
            </Form.Item>
            <Form.Item
              name="end_date"
              label={t("campaigns.dateTo")}
              rules={[{ required: true, message: t("campaigns.selectDatesRequired") }]}
              dependencies={["start_date"]}
            >
              <DatePicker
                style={{ width: "100%" }}
                placeholder={t("campaigns.selectDateTo")}
                onChange={(date) => handleDateChange(date, null, "end_date")}
                disabledDate={(current) => {
                  const startDate = form.getFieldValue("start_date");
                  return startDate && current && current < startDate;
                }}
              />
            </Form.Item>
          </>
        )}

        <CitiesSelector
          required={true}
          readOnly={false}
          mode="multiple"
          label={t("campaigns.cities")}
        />

        <Form.Item
          name="is_active"
          label={t("campaigns.status")}
          valuePropName="checked"
        >
          <Switch
            checkedChildren={t("campaigns.active")}
            unCheckedChildren={t("campaigns.inactive")}
          />
        </Form.Item>

        <Form.Item
          name="is_recurring"
          label={t("campaigns.isRecurring")}
          valuePropName="checked"
          tooltip={t("campaigns.isRecurringTooltip")}
        >
          <Switch
            checkedChildren={t("campaigns.recurring")}
            unCheckedChildren={t("campaigns.oneTime")}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CampaignEditForm;
